import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Icon, Segment, Confirm, Form } from 'semantic-ui-react';
import { createAjv } from '@jsonforms/core';

// Helper function to get localized string from window.l (lab_translations)
const t = (key, defaultValue = '', interpolation = {}) => {
  try {
    const keys = key.split('.');
    let value = typeof window !== 'undefined' && window.l;

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        value = null;
        break;
      }
    }

    if (typeof value === 'string' && interpolation) {
      // Simple interpolation for %{variable} patterns
      return value.replace(/%{(\w+)}/g, (match, varName) => {
        return interpolation[varName] !== undefined ? interpolation[varName] : match;
      });
    }

    return value || defaultValue;
  } catch (e) {
    return defaultValue;
  }
};

/**
 * Simple JsonFormsEditor - Custom implementation that works reliably
 *
 * Instead of using JSONForms' complex renderer system, we build a simple
 * form renderer that respects JSON Schema validation.
 */
const JsonFormsEditor = ({
  schema,
  uiSchema,
  data,
  onSave,
  onClose,
  title = 'Edit Properties',
  open = false,
  locale = 'en'
}) => {
  // Local state for the form data
  const [formData, setFormData] = useState(data || {});
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState([]);

  // Create AJV instance for validation
  const ajv = createAjv({
    useDefaults: true,
    strict: false,
    allErrors: true,
    verbose: true,
    coerceTypes: true
  });

  // Update local form data when data prop changes (modal opens)
  useEffect(() => {
    setFormData(data || {});
    setHasChanges(false);
    setErrors([]);
  }, [data, open]);

  // Handle field change
  const handleChange = useCallback((fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    setHasChanges(true);
  }, []);

  // Handle save button click
  const handleSave = useCallback(() => {
    const valid = ajv.validate(schema, formData);
    if (valid) {
      onSave(formData);
      setHasChanges(false);
      onClose();
    } else {
      console.error('[JsonFormsEditor] Validation errors:', ajv.errors);
      setErrors(ajv.errors);
    }
  }, [formData, schema, onSave, onClose, ajv]);

  // Handle close button click
  const handleClose = useCallback(() => {
    if (hasChanges) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  }, [hasChanges, onClose]);

  // Handle confirm discard changes
  const handleConfirmDiscard = useCallback(() => {
    setShowConfirm(false);
    onClose();
  }, [onClose]);

  // Handle cancel discard
  const handleCancelDiscard = useCallback(() => {
    setShowConfirm(false);
  }, []);

  // Handle click outside modal
  const handleDimmerClick = useCallback(() => {
    handleClose();
  }, [handleClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && open) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, handleClose]);

  // Get error message for a field
  const getFieldError = (fieldName) => {
    const fieldError = errors.find(e => e.instancePath === '/' + fieldName);
    return fieldError ? fieldError.message : null;
  };

  // Render a single form field based on schema
  const renderField = (fieldName, fieldSchema, uiControl = null) => {
    const value = formData[fieldName];
    const error = getFieldError(fieldName);
    const label = fieldSchema.title || fieldName;
    const isRequired = schema.required?.includes(fieldName);
    const controlOptions = uiControl?.options || {};

    // Enum = dropdown select
    if (fieldSchema.enum) {
      // Build color-coded options for LED color picker
      let dropdownOptions = fieldSchema.enumNames
        ? fieldSchema.enum.map((val, i) => ({ key: val, value: val, text: fieldSchema.enumNames[i] }))
        : fieldSchema.enum.map((val) => ({ key: val, value: val, text: val }));

      // Add color styling for LED color picker
      if (controlOptions.className === 'led-color-select') {
        const colorMap = {
          'red_1.8': { color: '#ff4444', style: { color: '#ff4444' } },
          'orange_2.2': { color: '#ff8800', style: { color: '#ff8800' } },
          'yellow_2.1': { color: '#ffcc00', style: { color: '#ffcc00' } },
          'green_2.0': { color: '#00cc00', style: { color: '#00cc00' } },
          'blue_3.5': { color: '#0066ff', style: { color: '#0066ff' } },
          'white_3.6': { color: '#ffffff', style: { color: '#333', backgroundColor: '#fff', border: '1px solid #ccc' } }
        };
        const colorLabel = (val, label) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                display: 'inline-block',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: colorMap[val]?.color || '#ccc',
                border: '1px solid rgba(0,0,0,0.2)',
                flexShrink: 0
              }}
            />
            {label}
          </div>
        );
        dropdownOptions = dropdownOptions.map(opt => ({
          ...opt,
          style: colorMap[opt.value]?.style,
          text: colorLabel(opt.value, opt.text),
          content: colorLabel(opt.value, opt.text)
        }));
      }

      return (
        <Form.Field key={fieldName} error={!!error} required={isRequired}>
          <label>{label}</label>
          <Form.Dropdown
            selection
            options={dropdownOptions}
            value={value ?? fieldSchema.default ?? fieldSchema.enum[0]}
            onChange={(e, { value }) => handleChange(fieldName, value)}
            error={error ? { content: error, pointing: 'below' } : null}
            fluid
            style={{ backgroundColor: 'white' }}
            className={controlOptions.className || ''}
          />
          {fieldSchema.description && (
            <small style={{ color: 'var(--muted-foreground)', display: 'block', marginTop: '4px' }}>
              {fieldSchema.description}
            </small>
          )}
        </Form.Field>
      );
    }

    // Boolean = checkbox
    if (fieldSchema.type === 'boolean') {
      return (
        <Form.Field key={fieldName} error={!!error} required={isRequired}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={value ?? fieldSchema.default ?? false}
              onChange={(e) => handleChange(fieldName, e.target.checked)}
              style={{ width: 'auto' }}
            />
            <span>{label}</span>
          </label>
          {fieldSchema.description && (
            <small style={{ color: 'var(--muted-foreground)', marginLeft: '20px' }}>
              {fieldSchema.description}
            </small>
          )}
          {error && <small style={{ color: '#dc3545', marginLeft: '20px' }}>{error}</small>}
        </Form.Field>
      );
    }

    // Number = number input
    if (fieldSchema.type === 'number' || fieldSchema.type === 'integer') {
      const stepValue = fieldSchema.type === 'integer' ? '1' : 'any';
      // Ensure value is a number for proper display
      const displayValue = value ?? fieldSchema.default ?? 0;
      const numValue = typeof displayValue === 'string' ? parseFloat(displayValue) : displayValue;

      return (
        <Form.Field key={fieldName} error={!!error} required={isRequired}>
          <label>{label}</label>
          <Form.Input
            type="number"
            step={stepValue}
            min={fieldSchema.minimum}
            max={fieldSchema.maximum}
            value={numValue}
            onChange={(e, { value }) => {
              const parsedValue = fieldSchema.type === 'integer' ? parseInt(value) : parseFloat(value);
              handleChange(fieldName, parsedValue);
            }}
            error={error ? { content: error, pointing: 'below' } : null}
            fluid
            input={{ style: { backgroundColor: 'white', color: 'black' }, step: stepValue }}
          />
          {fieldSchema.description && (
            <small style={{ color: 'var(--muted-foreground)', display: 'block', marginTop: '4px' }}>
              {fieldSchema.description}
            </small>
          )}
        </Form.Field>
      );
    }

    // String = text input (default)
    return (
      <Form.Field key={fieldName} error={!!error} required={isRequired}>
        <label>{label}</label>
        <Form.Input
          placeholder={fieldSchema.placeholder || ''}
          value={value ?? fieldSchema.default ?? ''}
          onChange={(e, { value }) => handleChange(fieldName, value)}
          error={error ? { content: error, pointing: 'below' } : null}
          fluid
          input={{ style: { backgroundColor: 'white', color: 'black' } }}
        />
        {fieldSchema.description && (
          <small style={{ color: 'var(--muted-foreground)', display: 'block', marginTop: '4px' }}>
            {fieldSchema.description}
          </small>
        )}
      </Form.Field>
    );
  };

  // Process uiSchema groups if present, otherwise render flat
  const renderForm = () => {
    const properties = schema?.properties || {};
    const propertyNames = Object.keys(properties);

    if (!propertyNames.length) {
      return (
        <Segment placeholder style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--muted-foreground)' }}>{t('spice_input.no_fields_defined', 'No fields defined in schema.')}</p>
        </Segment>
      );
    }

    // If uiSchema has groups, render grouped
    if (uiSchema?.elements) {
      return uiSchema.elements.map((element, groupIndex) => {
        // Group element
        if (element.type === 'Group') {
          return (
            <div
              key={groupIndex}
              style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: 'var(--muted)',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)'
              }}
            >
              {element.label && (
                <h4 style={{
                  margin: '0 0 12px 0',
                  paddingBottom: '8px',
                  borderBottom: '1px solid var(--border)',
                  color: 'var(--foreground)'
                }}>
                  {element.label}
                </h4>
              )}
              {element.elements?.map((control, controlIndex) => {
                if (control.type === 'Control') {
                  const fieldName = control.scope?.replace('#/properties/', '');
                  if (fieldName && properties[fieldName]) {
                    return renderField(fieldName, properties[fieldName], control);
                  }
                }
                return null;
              })}
            </div>
          );
        }

        // Direct Control element (not in a group)
        if (element.type === 'Control') {
          const fieldName = element.scope?.replace('#/properties/', '');
          if (fieldName && properties[fieldName]) {
            return (
              <div key={groupIndex} style={{ marginBottom: '12px' }}>
                {renderField(fieldName, properties[fieldName], element)}
              </div>
            );
          }
        }

        return null;
      });
    }

    // Flat render (no uiSchema groups)
    return propertyNames.map(name => (
      <div key={name} style={{ marginBottom: '12px' }}>
        {renderField(name, properties[name])}
      </div>
    ));
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        dimmer="inverted"
        size="small"
        closeOnDimmerClick={true}
        onDimmerClick={handleDimmerClick}
        style={{
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        <Modal.Header style={{
          borderBottom: '1px solid var(--border)',
          padding: '16px 20px',
          backgroundColor: 'var(--muted)',
          borderTopLeftRadius: 'var(--radius)',
          borderTopRightRadius: 'var(--radius)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name="edit" size="large" style={{ marginRight: '10px', color: 'var(--primary)' }} />
              <span style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--foreground)' }}>
                {title}
              </span>
            </div>
            <Icon
              name="close"
              link
              onClick={handleClose}
              style={{ cursor: 'pointer', color: 'var(--muted-foreground)' }}
            />
          </div>
        </Modal.Header>

        <Modal.Content style={{
          padding: '20px',
          backgroundColor: 'var(--card)',
          maxHeight: '70vh',
          overflowY: 'auto',
        }}>
          <style>{`
            /* Theme-aware styles for JSON Forms Editor modal inputs
               Uses theme CSS variables for light/dark mode compatibility */
            .jsonforms-editor-wrapper.ui.form input[type="text"],
            .jsonforms-editor-wrapper.ui.form input[type="number"],
            .jsonforms-editor-wrapper.ui.form .ui.input input,
            .jsonforms-editor-wrapper.ui.form .ui.input input[type="text"],
            .jsonforms-editor-wrapper.ui.form .ui.input input[type="number"] {
              background-color: var(--background) !important;
              color: var(--foreground) !important;
              caret-color: var(--foreground) !important;
              border: 1px solid var(--border) !important;
              box-shadow: none !important;
              padding: 0.67857143em 1em !important;
            }
            .jsonforms-editor-wrapper.ui.form input[type="text"]:focus,
            .jsonforms-editor-wrapper.ui.form input[type="number"]:focus,
            .jsonforms-editor-wrapper.ui.form .ui.input input:focus {
              background-color: var(--background) !important;
              color: var(--foreground) !important;
              border-color: var(--primary) !important;
            }
            .jsonforms-editor-wrapper.ui.form .ui.dropdown,
            .jsonforms-editor-wrapper.ui.form .ui.selection.dropdown {
              background-color: var(--background) !important;
              border: 1px solid var(--border) !important;
            }
            .jsonforms-editor-wrapper.ui.form .ui.dropdown .menu,
            .jsonforms-editor-wrapper.ui.form .ui.selection.dropdown .menu {
              background-color: var(--popover) !important;
              border: 1px solid var(--border) !important;
            }
            .jsonforms-editor-wrapper.ui.form .ui.dropdown .item,
            .jsonforms-editor-wrapper.ui.form .ui.selection.dropdown .item {
              color: var(--popover-foreground) !important;
              background-color: transparent !important;
            }
            .jsonforms-editor-wrapper.ui.form .ui.dropdown .item:hover,
            .jsonforms-editor-wrapper.ui.form .ui.selection.dropdown .item:hover,
            .jsonforms-editor-wrapper.ui.form .ui.dropdown .item.selected,
            .jsonforms-editor-wrapper.ui.form .ui.selection.dropdown .item.selected {
              background-color: var(--muted) !important;
            }
            .jsonforms-editor-wrapper.ui.form .ui.dropdown .text,
            .jsonforms-editor-wrapper.ui.form .ui.selection.dropdown .text,
            .jsonforms-editor-wrapper.ui.form .ui.dropdown .default.text,
            .jsonforms-editor-wrapper.ui.form .ui.selection.dropdown .default.text {
              color: var(--foreground) !important;
            }
            .jsonforms-editor-wrapper.ui.form .ui.dropdown .dropdown.icon,
            .jsonforms-editor-wrapper.ui.form .ui.selection.dropdown .dropdown.icon {
              color: var(--muted-foreground) !important;
            }
            .jsonforms-editor-wrapper.ui.form label {
              color: var(--foreground) !important;
              font-weight: 500 !important;
            }
            .jsonforms-editor-wrapper.ui.form .field.error label {
              color: var(--destructive) !important;
            }
            .jsonforms-editor-wrapper.ui.form .field.error input,
            .jsonforms-editor-wrapper.ui.form .field.error .ui.dropdown {
              border-color: var(--destructive) !important;
            }
          `}</style>
          {schema && Object.keys(schema).length > 0 ? (
            <Form className="jsonforms-editor-wrapper ui form">{renderForm()}</Form>
          ) : (
            <Segment placeholder style={{ textAlign: 'center', padding: '40px' }}>
              <Icon name="file code outline" size="huge" style={{ color: 'var(--muted-foreground)' }} />
              <p style={{ color: 'var(--muted-foreground)', marginTop: '15px' }}>
                {t('spice_input.no_schema_provided', 'No schema provided for this form.')}
              </p>
            </Segment>
          )}
        </Modal.Content>

        <Modal.Actions style={{
          borderTop: '1px solid var(--border)',
          padding: '16px 20px',
          backgroundColor: 'var(--muted)',
          borderBottomLeftRadius: 'var(--radius)',
          borderBottomRightRadius: 'var(--radius)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
        }}>
          <Button
            onClick={handleClose}
            style={{
              backgroundColor: 'transparent',
              color: 'var(--foreground)',
              border: '1px solid var(--border)',
            }}
          >
            {t('spice_input.cancel', 'Cancel')}
          </Button>
          <Button
            primary
            onClick={handleSave}
            style={{
              backgroundColor: 'var(--primary)',
              color: 'white',
            }}
          >
            {t('spice_input.ok', 'Ok')}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Confirmation dialog when there are unsaved changes */}
      <Confirm
        open={showConfirm}
        content={t('spice_input.unsaved_changes', 'You have unsaved changes. Are you sure you want to close without saving?')}
        cancelButton={t('spice_input.cancel', 'Cancel')}
        confirmButton={t('spice_input.discard_changes', 'Discard Changes')}
        onCancel={handleCancelDiscard}
        onConfirm={handleConfirmDiscard}
        style={{
          borderRadius: 'var(--radius)',
          backgroundColor: 'var(--card)',
          color: 'var(--foreground)',
        }}
      />
    </>
  );
};

export default JsonFormsEditor;
